import React, { useEffect, useMemo, useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { type Lease } from "../../../store/api/leasesApi";
import type { Payment } from "../../../store/api/paymentsApi";
import { useListPaymentsQuery } from "../../../store/api/paymentsApi";
import { useGetPublicPropertyByIdQuery } from "../../../store/api/publicPropertiesApi";
import {
    useListTenancyAgreementsQuery,
    AgreementStatus,
} from "../../../store/api/tenancyAgreementsApi";
import UploadTenancyAgreementModal from "./UploadTenancyAgreementModal";

interface LeaseDetailPanelProps {
	lease: Lease | null;
	isOpen: boolean;
	onClose: () => void;
	onEdit: (lease: Lease) => void;
	onRenew?: (leaseId: string) => void;
	onGenerateDocument?: (leaseId: string) => void;
	onSendNotification?: (leaseId: string) => void;
}

const LeaseDetailPanel: React.FC<LeaseDetailPanelProps> = ({
	lease,
	isOpen,
	onClose,
	onEdit,
	onRenew,
	onGenerateDocument,
	onSendNotification,
}) => {
	const [activeTab, setActiveTab] = useState("overview");
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [isUploadAgreementModalOpen, setIsUploadAgreementModalOpen] = useState(false);

	// Reset image index when lease changes or modal opens
	useEffect(() => {
		if (isOpen && lease) {
			setCurrentImageIndex(0);
			setActiveTab("overview");
		}
	}, [lease?.id, isOpen]);

	// Fetch property details
	const { data: propertyData } = useGetPublicPropertyByIdQuery(
		lease?.property_id || "",
		{ skip: !lease?.property_id || !isOpen },
	);

	// Fetch payments for this lease
	const { data: paymentsData, error: paymentsError } = useListPaymentsQuery(
		{ lease: lease?.id, page_size: 1000 },
		{ skip: !lease?.id || !isOpen },
	);

	// Fetch tenancy agreements for this lease
	const { data: agreementsData } = useListTenancyAgreementsQuery(
		{ lease_id: lease?.id, page_size: 100 },
		{ skip: !lease?.id || !isOpen },
	);

	// Log errors for debugging
	if (paymentsError) {
		console.error("Error fetching payments:", paymentsError);
	}

	// Filter payments for this lease
	const leasePayments = useMemo(() => {
		if (!paymentsData?.results || !lease?.id) return [];
		return paymentsData.results.filter((payment: Payment) => {
			const paymentLeaseId = payment.lease_id;
			return paymentLeaseId === lease.id;
		});
	}, [paymentsData, lease?.id]);

	// Calculate days to expiry
	const daysToExpiry = useMemo(() => {
		if (!lease?.end_date) return 0;
		const endDate = new Date(lease.end_date);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		endDate.setHours(0, 0, 0, 0);
		const diffTime = endDate.getTime() - today.getTime();
		return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	}, [lease?.end_date]);

	// Calculate lease term in months
	const leaseTerm = useMemo(() => {
		if (!lease?.start_date || !lease?.end_date) return "N/A";
		const start = new Date(lease.start_date);
		const end = new Date(lease.end_date);
		const months = Math.ceil(
			(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30),
		);
		return `${months} months`;
	}, [lease?.start_date, lease?.end_date]);

	// Early return check - MUST be after all hooks
	if (!isOpen || !lease) return null;

	const formatCurrency = (amount: number): string => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	const formatDate = (dateString: string | null | undefined): string => {
		if (!dateString) return "N/A";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getStatusColor = (status: string): string => {
		switch (status?.toLowerCase()) {
			case "active":
				return daysToExpiry <= 30
					? "bg-error/10 text-error border-error/20"
					: daysToExpiry <= 60
					? "bg-warning/10 text-warning border-warning/20"
					: "bg-success/10 text-success border-success/20";
			case "draft":
				return "bg-muted/30 text-foreground border border-border";
			case "expired":
				return "bg-error/20 text-error border border-error/30";
			case "terminated":
				return "bg-muted/30 text-foreground border border-border";
			case "renewed":
				return "bg-success/20 text-success border border-success/30";
			default:
				return "bg-muted/30 text-foreground border border-border";
		}
	};

	const tabs = [
		{ id: "overview", label: "Overview", icon: "FileText" },
		{ id: "payments", label: "Payments", icon: "Currency" },
		{ id: "documents", label: "Documents", icon: "Paperclip" },
		{ id: "history", label: "History", icon: "History" },
	];

	const renderOverview = () => {
		if (!lease) return null;

		try {
			return (
				<div className="space-y-6">
					{/* Property Images Carousel */}
					{propertyData?.images && propertyData.images.length > 0 ? (
						<div className="relative">
							<div className="relative h-64 rounded-lg overflow-hidden bg-muted">
								<Image
									src={propertyData.images[currentImageIndex]}
									alt={`${
										propertyData.address ||
										propertyData.name ||
										"Property"
									} - Image ${currentImageIndex + 1}`}
									className="w-full h-full object-cover"
								/>

								{/* Navigation Arrows */}
								{propertyData.images.length > 1 && (
									<>
										<button
											onClick={(e) => {
												e.stopPropagation();
												setCurrentImageIndex((prev) =>
													prev > 0 ? prev - 1 : propertyData.images.length - 1,
												);
											}}
											className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition z-10"
											aria-label="Previous image"
										>
											<Icon name="ChevronLeft" size={20} />
										</button>
										<button
											onClick={(e) => {
												e.stopPropagation();
												setCurrentImageIndex((prev) =>
													prev < propertyData.images.length - 1 ? prev + 1 : 0,
												);
											}}
											className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white smooth-transition z-10"
											aria-label="Next image"
										>
											<Icon name="ChevronRight" size={20} />
										</button>
									</>
								)}

								{/* Image Counter */}
								{propertyData.images.length > 1 && (
									<div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/50 text-white text-xs font-medium z-10">
										{currentImageIndex + 1} / {propertyData.images.length}
									</div>
								)}
							</div>

							{/* Thumbnail Strip */}
							{propertyData.images.length > 1 && (
								<div className="mt-3 flex gap-2 overflow-x-auto pb-2">
									{propertyData.images.map((image, index) => (
										<button
											key={index}
											onClick={() => setCurrentImageIndex(index)}
											className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 smooth-transition ${
												index === currentImageIndex
													? "border-primary"
													: "border-border hover:border-primary/50"
											}`}
											aria-label={`View image ${index + 1}`}
										>
											<Image
												src={image}
												alt={`Thumbnail ${index + 1}`}
												className="w-full h-full object-cover"
											/>
										</button>
									))}
								</div>
							)}
						</div>
					) : (
						<div className="h-64 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
							<Icon
								name="Building2"
								size={48}
								className="text-muted-foreground"
							/>
						</div>
					)}

					{/* Lease Information */}
					<div className="grid grid-cols-2 gap-4">
						<div>
							<h4 className="font-medium text-foreground mb-2">
								Lease Details
							</h4>
							<div className="space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Lease Code:</span>
									<span className="text-foreground font-mono">
										{lease.lease_code || "N/A"}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Status:</span>
									<span
										className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
											lease.status,
										)}`}
									>
										{lease.status.charAt(0).toUpperCase() +
											lease.status.slice(1)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Term:</span>
									<span className="text-foreground">{leaseTerm}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Days to Expiry:</span>
									<span
										className={`font-medium ${
											daysToExpiry <= 30
												? "text-error"
												: daysToExpiry <= 60
												? "text-warning"
												: "text-foreground"
										}`}
									>
										{daysToExpiry > 0 ? `${daysToExpiry} days` : "Expired"}
									</span>
								</div>
							</div>
						</div>

						<div>
							<h4 className="font-medium text-foreground mb-2">Financial</h4>
							<div className="space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Monthly Rent:</span>
									<span className="text-foreground font-medium">
										{formatCurrency(Number(lease.monthly_rent) || 0)}
									</span>
								</div>
								{lease.security_deposit && (
									<div className="flex justify-between">
										<span className="text-muted-foreground">
											Security Deposit:
										</span>
										<span className="text-foreground font-medium">
											{formatCurrency(Number(lease.security_deposit))}
										</span>
									</div>
								)}
								<div className="flex justify-between">
									<span className="text-muted-foreground">Start Date:</span>
									<span className="text-foreground">
										{formatDate(lease.start_date)}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">End Date:</span>
									<span className="text-foreground">
										{formatDate(lease.end_date)}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Tenant Information */}
					<div>
						<h4 className="font-medium text-foreground mb-2">Tenant</h4>
						<div className="bg-muted/30 rounded-lg p-4">
							<div className="flex items-center space-x-3">
								<div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
									<Icon name="User" size={24} />
								</div>
								<div>
									<p className="font-medium text-foreground">
										{lease.tenant_name || "Unknown Tenant"}
									</p>
									{lease.tenant_email && (
										<p className="text-sm text-muted-foreground">
											{lease.tenant_email}
										</p>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Property Information */}
					{propertyData && (
						<div>
							<h4 className="font-medium text-foreground mb-2">Property</h4>
							<div className="bg-muted/30 rounded-lg p-4">
								<p className="font-medium text-foreground">
									{propertyData.name ||
										lease.property_name ||
										"Unknown Property"}
								</p>
								{propertyData.address && (
									<p className="text-sm text-muted-foreground mt-1">
										{propertyData.address}
									</p>
								)}
								{propertyData.bedrooms && propertyData.bathrooms && (
									<p className="text-sm text-muted-foreground mt-1">
										{propertyData.bedrooms} Bedrooms, {propertyData.bathrooms}{" "}
										Bathrooms
									</p>
								)}
							</div>
						</div>
					)}

					{/* Terms */}
					{lease.terms && (
						<div>
							<h4 className="font-medium text-foreground mb-2">
								Terms & Conditions
							</h4>
							<div className="bg-muted/30 rounded-lg p-4">
								<p className="text-sm text-muted-foreground whitespace-pre-wrap">
									{lease.terms}
								</p>
							</div>
						</div>
					)}
				</div>
			);
		} catch (error) {
			console.error("Error rendering overview:", error);
			return (
				<div className="text-center py-8 text-muted-foreground">
					Error loading lease overview
				</div>
			);
		}
	};

	const renderPayments = () => {
		if (!leasePayments || leasePayments.length === 0) {
			return (
				<div className="text-center py-8 text-muted-foreground">
					No payments found for this lease
				</div>
			);
		}

		return (
			<div className="space-y-4">
				{leasePayments.map((payment: Payment) => (
					<div key={payment.id} className="bg-muted/30 rounded-lg p-4">
						<div className="flex items-center justify-between mb-3">
							<div>
								<p className="font-medium text-foreground">
									{formatCurrency(Number(payment.amount) || 0)}
								</p>
								<p className="text-sm text-muted-foreground">
									Due: {formatDate(payment.due_date)}
									{payment.paid_date &&
										` • Paid: ${formatDate(payment.paid_date)}`}
								</p>
							</div>
							<span
								className={`px-2 py-1 rounded-full text-xs font-medium ${
									payment.status === "paid"
										? "bg-success/10 text-success"
										: payment.status === "overdue"
										? "bg-error/10 text-error"
										: "bg-warning/10 text-warning"
								}`}
							>
								{payment.status?.charAt(0).toUpperCase() +
									payment.status?.slice(1) || "Pending"}
							</span>
						</div>
						{payment.payment_method && (
							<p className="text-sm text-muted-foreground">
								Method: {payment.payment_method}
							</p>
						)}
						{payment.reference_number && (
							<p className="text-sm text-muted-foreground">
								Reference: {payment.reference_number}
							</p>
						)}
					</div>
				))}
			</div>
		);
	};

	const renderDocuments = () => {
		const agreements = agreementsData?.results || [];
		const leaseDocs = lease.documents || [];

		if (agreements.length === 0 && leaseDocs.length === 0) {
			return (
				<div className="text-center py-8 text-muted-foreground">
					<p>No documents available for this lease</p>
					<Button
						variant="outline"
						className="mt-4"
						onClick={() => setIsUploadAgreementModalOpen(true)}
						iconName="Upload"
					>
						Upload Tenancy Agreement
					</Button>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{/* Tenancy Agreements Section */}
				{agreements.length > 0 && (
					<div>
						<div className="flex items-center justify-between mb-4">
							<h4 className="font-medium text-foreground">Tenancy Agreements</h4>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setIsUploadAgreementModalOpen(true)}
								iconName="Upload"
							>
								Upload New
							</Button>
						</div>
						<div className="space-y-4">
							{agreements.map((agreement) => (
								<div key={agreement.id} className="bg-muted/30 rounded-lg p-4">
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center space-x-3">
											<div className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
												<Icon name="FileText" size={20} />
											</div>
											<div>
												<p className="font-medium text-foreground">
													Tenancy Agreement v{agreement.version}
												</p>
												<p className="text-sm text-muted-foreground">
													Uploaded: {formatDate(agreement.uploaded_at)}
													{agreement.uploaded_by_name &&
														` by ${agreement.uploaded_by_name}`}
												</p>
											</div>
										</div>
										<div className="flex items-center space-x-2">
											<span
												className={`px-2 py-1 rounded-full text-xs font-medium ${
													agreement.status === AgreementStatus.ACCEPTED
														? "bg-success/10 text-success"
														: agreement.status === AgreementStatus.PENDING_CHANGES
														? "bg-warning/10 text-warning"
														: agreement.status === AgreementStatus.REJECTED
														? "bg-error/10 text-error"
														: "bg-muted/10 text-muted-foreground"
												}`}
											>
												{agreement.status.replace("_", " ")}
											</span>
											<Button
												variant="outline"
												size="sm"
												onClick={() => window.open(agreement.document_url, "_blank")}
												iconName="ExternalLink"
											>
												View
											</Button>
										</div>
									</div>
									{agreement.tenant_initials && (
										<p className="text-sm text-muted-foreground">
											Signed by tenant: {agreement.tenant_initials} on{" "}
											{formatDate(agreement.tenant_signed_at)}
										</p>
									)}
									{agreement.tenant_notes && (
										<div className="mt-2 p-2 bg-warning/10 rounded text-sm text-warning">
											<strong>Tenant Notes:</strong> {agreement.tenant_notes}
										</div>
									)}
									{agreement.admin_notes && (
										<div className="mt-2 p-2 bg-muted rounded text-sm text-muted-foreground">
											<strong>Admin Notes:</strong> {agreement.admin_notes}
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				)}

				{/* Other Lease Documents Section */}
				{leaseDocs.length > 0 && (
					<div>
						<h4 className="font-medium text-foreground mb-4">Other Documents</h4>
						<div className="space-y-4">
							{leaseDocs.map((docUrl: string, index: number) => {
								try {
									const url = new URL(docUrl);
									const fileName = url.pathname.split("/").pop() || "Lease Document";
									return (
										<div key={index} className="bg-muted/30 rounded-lg p-4">
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-3">
													<div className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
														<Icon name="FileText" size={20} />
													</div>
													<div>
														<p className="font-medium text-foreground">
															Document {index + 1}
														</p>
														<p className="text-sm text-muted-foreground">
															{fileName}
														</p>
													</div>
												</div>
												<Button
													variant="outline"
													size="sm"
													onClick={() => window.open(docUrl, "_blank")}
													iconName="ExternalLink"
												>
													View
												</Button>
											</div>
										</div>
									);
								} catch {
									// If URL parsing fails, just show the index
									return (
										<div key={index} className="bg-muted/30 rounded-lg p-4">
											<div className="flex items-center justify-between">
												<div className="flex items-center space-x-3">
													<div className="w-10 h-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
														<Icon name="FileText" size={20} />
													</div>
													<div>
														<p className="font-medium text-foreground">
															Document {index + 1}
														</p>
														<p className="text-sm text-muted-foreground">
															Lease Document
														</p>
													</div>
												</div>
												<Button
													variant="outline"
													size="sm"
													onClick={() => window.open(docUrl, "_blank")}
													iconName="ExternalLink"
												>
													View
												</Button>
											</div>
										</div>
									);
								}
							})}
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderHistory = () => {
		// Lease history would include status changes, renewals, etc.
		// For now, show basic lease timeline
		return (
			<div className="space-y-4">
				<div className="bg-muted/30 rounded-lg p-4">
					<div className="flex items-center space-x-3 mb-2">
						<div className="w-2 h-2 bg-primary rounded-full"></div>
						<div>
							<p className="font-medium text-foreground">Lease Created</p>
							<p className="text-sm text-muted-foreground">
								{formatDate(lease.created_at)}
							</p>
						</div>
					</div>
				</div>

				{lease.start_date && (
					<div className="bg-muted/30 rounded-lg p-4">
						<div className="flex items-center space-x-3 mb-2">
							<div className="w-2 h-2 bg-success rounded-full"></div>
							<div>
								<p className="font-medium text-foreground">Lease Started</p>
								<p className="text-sm text-muted-foreground">
									{formatDate(lease.start_date)}
								</p>
							</div>
						</div>
					</div>
				)}

				{lease.end_date && (
					<div className="bg-muted/30 rounded-lg p-4">
						<div className="flex items-center space-x-3 mb-2">
							<div
								className={`w-2 h-2 rounded-full ${
									daysToExpiry > 0 ? "bg-warning" : "bg-error"
								}`}
							></div>
							<div>
								<p className="font-medium text-foreground">Lease End Date</p>
								<p className="text-sm text-muted-foreground">
									{formatDate(lease.end_date)}
									{daysToExpiry > 0 && ` (${daysToExpiry} days remaining)`}
								</p>
							</div>
						</div>
					</div>
				)}

				{lease.updated_at && lease.updated_at !== lease.created_at && (
					<div className="bg-muted/30 rounded-lg p-4">
						<div className="flex items-center space-x-3 mb-2">
							<div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
							<div>
								<p className="font-medium text-foreground">Last Updated</p>
								<p className="text-sm text-muted-foreground">
									{formatDate(lease.updated_at)}
								</p>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderTabContent = () => {
		try {
			switch (activeTab) {
				case "overview":
					return renderOverview();
				case "payments":
					return renderPayments();
				case "documents":
					return renderDocuments();
				case "history":
					return renderHistory();
				default:
					return renderOverview();
			}
		} catch (error) {
			console.error("Error rendering tab content:", error);
			return (
				<div className="text-center py-8 text-muted-foreground">
					Error loading content. Please try again.
				</div>
			);
		}
	};

	try {
		return (
			<>
				{/* Backdrop */}
				<div
					className="fixed inset-0 bg-black/50 z-1100 animate-fade-in"
					onClick={onClose}
				/>

				{/* Panel */}
				<div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-card border-l border-border z-1200 animate-slide-in-right overflow-hidden flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-border">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Lease {lease.lease_code || lease.id}
							</h2>
							<p className="text-sm text-muted-foreground">
								{lease.tenant_name || "Unknown Tenant"}
							</p>
						</div>
						<div className="flex items-center space-x-2">
							{onRenew && lease.status === "active" && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => onRenew(lease.id)}
									iconName="RefreshCw"
									iconPosition="left"
								>
									Renew
								</Button>
							)}
							<Button
								variant="outline"
								size="sm"
								onClick={() => setIsUploadAgreementModalOpen(true)}
								iconName="Upload"
								iconPosition="left"
							>
								Upload Agreement
							</Button>
							{onGenerateDocument && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => onGenerateDocument(lease.id)}
									iconName="FileText"
									iconPosition="left"
								>
									Documents
								</Button>
							)}
							<Button
								variant="outline"
								size="sm"
								onClick={() => onEdit(lease)}
								iconName="Edit"
								iconPosition="left"
							>
								Edit
							</Button>
							<Button variant="ghost" size="icon" onClick={onClose}>
								<Icon name="X" size={20} />
							</Button>
						</div>
					</div>

					{/* Tabs */}
					<div className="flex border-b border-border">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 smooth-transition ${
									activeTab === tab.id
										? "border-primary text-primary"
										: "border-transparent text-muted-foreground hover:text-foreground"
								}`}
							>
								<Icon name={tab.icon as any} size={16} />
								<span>{tab.label}</span>
							</button>
						))}
					</div>

					{/* Content */}
					<div className="flex-1 overflow-y-auto p-6">{renderTabContent()}</div>
				</div>

				{/* Upload Agreement Modal */}
				{lease && (
					<UploadTenancyAgreementModal
						isOpen={isUploadAgreementModalOpen}
						onClose={() => setIsUploadAgreementModalOpen(false)}
						lease={lease}
					/>
				)}
			</>
		);
	} catch (error: any) {
		console.error("Error rendering LeaseDetailPanel:", error);
		return (
			<>
				<div
					className="fixed inset-0 bg-black/50 z-1100 animate-fade-in"
					onClick={onClose}
				/>
				<div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-card border-l border-border z-1200 animate-slide-in-right overflow-hidden flex flex-col">
					<div className="flex items-center justify-between p-6 border-b border-border">
						<h2 className="text-xl font-semibold text-foreground">Error</h2>
						<Button variant="ghost" size="icon" onClick={onClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>
					<div className="flex-1 overflow-y-auto p-6">
						<div className="text-center py-8 text-muted-foreground">
							<p>An error occurred while loading lease details.</p>
							<p className="mt-2 text-sm">
								Please try again or contact support.
							</p>
						</div>
					</div>
				</div>
			</>
		);
	}
};

export default LeaseDetailPanel;
